/**
 * Partition multigroup Funciones display rows by child group + membership dates.
 * Canonical plan_assignments stay 1:1; this only builds display sections.
 */

export const MULTIGROUP_FALLBACK_COLORS = [
	"#8B7CFF",
	"#6FBF73",
	"#E8B84A",
	"#E39BB0",
	"#6BA3C7",
];

export function resolveMultigroupSectionColor(group, index = 0) {
	const color = group?.color && String(group.color).trim();
	if (color) return color;
	return MULTIGROUP_FALLBACK_COLORS[index % MULTIGROUP_FALLBACK_COLORS.length];
}

export function hexToRgbChannels(hex) {
	if (!hex || typeof hex !== "string") return null;
	let h = hex.replace("#", "").trim();
	if (h.length === 3) {
		h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
	}
	if (h.length !== 6) return null;
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	if ([r, g, b].some((n) => Number.isNaN(n))) return null;
	return { r, g, b };
}

export function hexToRgba(hex, alpha = 1) {
	const channels = hexToRgbChannels(hex);
	if (!channels) return `rgba(0, 0, 0, ${alpha})`;
	return `rgba(${channels.r}, ${channels.g}, ${channels.b}, ${alpha})`;
}

/** Opaque tint: color×alpha over white. Sticky columns need solid bg so scroll does not bleed through. */
export function mixHexWithWhite(hex, alpha = 1) {
	const channels = hexToRgbChannels(hex);
	const a = Math.min(1, Math.max(0, alpha));
	const mix = (c) => Math.round(c * a + 255 * (1 - a));
	if (!channels) {
		const g = mix(0);
		return `#${[g, g, g]
			.map((n) => n.toString(16).padStart(2, "0"))
			.join("")}`;
	}
	return `#${[mix(channels.r), mix(channels.g), mix(channels.b)]
		.map((n) => n.toString(16).padStart(2, "0"))
		.join("")}`;
}

export function getMultigroupSectionVars(group, index = 0) {
	const color = resolveMultigroupSectionColor(group, index);
	return {
		"--mg-color": color,
		"--mg-wash": mixHexWithWhite(color, 0.18),
		"--mg-wash-soft": mixHexWithWhite(color, 0.08),
	};
}

export function isDayInMembership(day, membership) {
	if (!day || !membership?.start_day) return false;
	if (day < membership.start_day) return false;
	if (membership.end_day && day > membership.end_day) return false;
	return true;
}

export function membershipOverlapsRange(membership, range) {
	if (!membership?.start_day || !range?.start || !range?.end) return false;
	if (membership.start_day > range.end) return false;
	if (membership.end_day && membership.end_day < range.start) return false;
	return true;
}

const counterValues = (accountAssignment) =>
	(accountAssignment?.counters || []).map((item) => {
		const value = parseFloat(item && item.value);
		return Number.isNaN(value) ? 0 : value;
	});

const zeroTotals = (width) => Array.from({ length: width }, () => 0);

const addTotals = (target, values) => {
	values.forEach((value, index) => {
		target[index] = (target[index] || 0) + value;
	});
	return target;
};

const maskPlans = (plans, membershipsForEmployee, groupId) =>
	(plans || []).map((plan) => {
		const onDay = (membershipsForEmployee || []).some(
			(membership) =>
				Number(membership.groupId) === Number(groupId) &&
				isDayInMembership(plan.day, membership)
		);
		if (onDay) return plan;
		return { ...plan, active: false };
	});

const employeeBelongsToGroup = (membershipsForEmployee, groupId, range) =>
	(membershipsForEmployee || []).some(
		(membership) =>
			Number(membership.groupId) === Number(groupId) &&
			membershipOverlapsRange(membership, range)
	);

export function buildMultigroupPlanSections({
	childGroups = [],
	assignments = [],
	accountAssignments = [],
	memberships = {},
	range,
} = {}) {
	const accountsByEmployeeId = new Map(
		(accountAssignments || []).map((item) => [item?.employee?.id, item])
	);
	const columnCount = (accountAssignments[0]?.counters || []).length;
	const uniqueSeen = new Set();
	const uniqueTotals = [];

	const sections = (childGroups || []).map((group) => {
		const members = [];

		assignments.forEach((assignment, sourceRowIndex) => {
			const employeeId = assignment?.employee?.id;
			if (employeeId == null) return;
			const employeeMemberships = memberships[employeeId] || [];
			if (!employeeBelongsToGroup(employeeMemberships, group.id, range)) {
				return;
			}
			members.push({
				sourceRowIndex,
				assignment: {
					...assignment,
					plans: maskPlans(
						assignment.plans,
						employeeMemberships,
						group.id
					),
				},
				accountAssignment: accountsByEmployeeId.get(employeeId) || null,
			});
		});

		const subtotals = zeroTotals(columnCount);
		members.forEach((row) => {
			addTotals(subtotals, counterValues(row.accountAssignment));
			if (!uniqueSeen.has(row.assignment.employee.id)) {
				uniqueSeen.add(row.assignment.employee.id);
				addTotals(uniqueTotals, counterValues(row.accountAssignment));
			}
		});

		return {
			group,
			rows: members,
			subtotals,
			memberCount: members.length,
		};
	});

	return { sections, uniqueTotals };
}
